import { CaretUpIcon, QuestionIcon } from "@phosphor-icons/react";
import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { useNavigation, NavigationProp, useRoute, RouteProp } from "@react-navigation/native";
import { RootStackParamList } from "../../../navigation/navigation.types";

import { Checkbox } from "../../../components/adminDashboard/SafetySupport/components/Checkbox";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { type GetCourseRosterInsightsQuery, CourseInsightsSentimentLabel } from "@axio/graphql/generated/graphql";
import { colors, radii, spacing } from "../../../styles/design-system";
import { clampPercentage, formatPercentage } from "../../../utils/analyticsUtils";

import { formatRiskLabel, getRiskBadgeStyle } from "./CourseRosterInsightsTable.utils";

type RosterInsightStudent = NonNullable<
  NonNullable<GetCourseRosterInsightsQuery["getCourseRosterInsights"]>["data"]
>["students"][number];

type CourseRosterInsightsTableProps = {
  students: RosterInsightStudent[];
  selectedStudentIds: string[];
  onSelectionChange: (studentIds: string[]) => void;
};

const formatSentimentLabel = (sentiment: CourseInsightsSentimentLabel): string => {
  if (sentiment === CourseInsightsSentimentLabel.Negative) return "Negative";
  if (sentiment === CourseInsightsSentimentLabel.Positive) return "Positive";
  return "Neutral";
};

export const toInitials = (value: string): string => {
  return value
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
};

export { getRiskBadgeStyle } from "./CourseRosterInsightsTable.utils";

const getSentimentBadgeStyle = (sentiment: CourseInsightsSentimentLabel) => {
  if (sentiment === CourseInsightsSentimentLabel.Negative) {
    return {
      backgroundColor: colors.error[50],
      borderColor: colors.error[200],
      textColor: colors.error[700],
    };
  }

  if (sentiment === CourseInsightsSentimentLabel.Positive) {
    return {
      backgroundColor: colors.success[50],
      borderColor: colors.success[200],
      textColor: colors.success[700],
    };
  }

  return {
    backgroundColor: colors.grey[50],
    borderColor: colors.grey[300],
    textColor: colors.grey[600],
  };
};

const getAssessmentTextColor = (value: number) => {
  if (value < 70) return colors.error[600];
  if (value < 85) return colors.warning[700];
  return colors.success[700];
};

const formatLastActive = (value?: string | null, now = Date.now()): string => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  const diffMs = now - date.getTime();

  if (diffMs < 0) return "—";

  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(hours / 24);

  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const CourseRosterInsightsTable: React.FC<CourseRosterInsightsTableProps> = ({
  students,
  selectedStudentIds,
  onSelectionChange,
}) => {
  const now = Date.now();
  const selectedStudentIdSet = new Set(selectedStudentIds);
  const allVisibleSelected =
    students.length > 0 && students.every((student) => selectedStudentIdSet.has(student.userId));

  const handleToggleAll = (checked: boolean) => {
    onSelectionChange(checked ? students.map((student) => student.userId) : []);
  };

  const handleToggleStudent = (studentId: string, checked: boolean) => {
    onSelectionChange(
      checked ? [...selectedStudentIds, studentId] : selectedStudentIds.filter((currentId) => currentId !== studentId)
    );
  };

  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Course Information For Particular Student">>();
  const { courseId, courseName } = route.params || {};

  const handleNavigateToStudentDetail = (studentId: string, studentName: string) => {
    navigation.navigate("Course Information For Particular Student", {
      courseId,
      courseName,
      studentId,
      studentName,
      returnTo: "Course Information",
    });
  };

  return (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        <View style={[styles.headerCell, styles.colCheckbox]}>
          <Checkbox checked={allVisibleSelected} onChange={handleToggleAll} size="sm" />
        </View>
        <View style={[styles.headerCell, styles.colStudent]}>
          <CustomText text="STUDENT" size="xs" weight="bold" useTranslationText={false} style={styles.headerLabel} />
        </View>
        <View style={[styles.headerCell, styles.colRisk]}>
          <View style={styles.headerInline}>
            <CustomText text="Risk" size="xs" weight="bold" useTranslationText={false} style={styles.headerLabel} />
            <QuestionIcon size={16} color={colors.grey[500]} weight="bold" />
          </View>
        </View>
        <View style={[styles.headerCell, styles.colProgress]}>
          <View style={styles.headerInline}>
            <CustomText text="Progress" size="xs" weight="bold" useTranslationText={false} style={styles.headerLabel} />
          </View>
        </View>
        <View style={[styles.headerCell, styles.colAssessments]}>
          <View style={styles.headerInline}>
            <CustomText
              text="Assessments"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.headerLabel}
            />
          </View>
        </View>
        <View style={[styles.headerCell, styles.colGrade]}>
          <View style={styles.headerInline}>
            <CustomText text="Grade" size="xs" weight="bold" useTranslationText={false} style={styles.headerLabel} />
          </View>
        </View>
        <View style={[styles.headerCell, styles.colSentiment]}>
          <View style={styles.headerInline}>
            <CustomText
              text="Sentiment"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.headerLabel}
            />
          </View>
        </View>
        <View style={[styles.headerCell, styles.colActive]}>
          <View style={styles.headerInline}>
            <CustomText
              text="Last Active"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.headerLabel}
            />
          </View>
        </View>
      </View>

      {students.length === 0 ? (
        <View style={styles.emptyState}>
          <CustomText
            text="No students match the current filters."
            size="s"
            useTranslationText={false}
            style={styles.mutedText}
          />
        </View>
      ) : (
        students.map((student, index) => {
          const riskBadge = getRiskBadgeStyle(student.riskLevel);
          const sentimentBadge = getSentimentBadgeStyle(student.sentiment);
          const progress = clampPercentage(student.progressPct);
          const assessmentColor = getAssessmentTextColor(student.assessmentTakenPct);
          const isSelected = selectedStudentIdSet.has(student.userId);
          const aggregateGradeText =
            typeof student.aggregateGradePct === "number" ? formatPercentage(student.aggregateGradePct) : "—";

          return (
            <Pressable
              key={student.userId}
              style={[
                styles.tableRow,
                isSelected && styles.selectedRow,
                index === students.length - 1 && styles.lastRow,
              ]}
              onPress={() => handleNavigateToStudentDetail(student.userId, student.studentName)}>
              <View style={[styles.bodyCell, styles.colCheckbox]}>
                <Checkbox
                  checked={isSelected}
                  onChange={(checked) => handleToggleStudent(student.userId, checked)}
                  size="sm"
                />
              </View>

              <View style={[styles.bodyCell, styles.colStudent]}>
                <View style={styles.studentCell}>
                  <View style={styles.avatar}>
                    <CustomText
                      text={toInitials(student.studentName)}
                      size="xs"
                      weight="bold"
                      useTranslationText={false}
                      style={styles.avatarText}
                    />
                  </View>
                  <CustomText text={student.studentName} size="s" weight="medium" useTranslationText={false} />
                </View>
              </View>

              <View style={[styles.bodyCell, styles.colRisk]}>
                <View
                  style={[
                    styles.pill,
                    { backgroundColor: riskBadge.backgroundColor, borderColor: riskBadge.borderColor },
                  ]}>
                  <CustomText
                    text={formatRiskLabel(student.riskLevel)}
                    size="xs"
                    weight="medium"
                    useTranslationText={false}
                    style={{ color: riskBadge.textColor }}
                  />
                </View>
              </View>

              <View style={[styles.bodyCell, styles.colProgress]}>
                <View style={styles.metricCell}>
                  <View style={styles.metricTrack}>
                    <View
                      style={[styles.metricFill, { width: `${progress}%`, backgroundColor: riskBadge.progressColor }]}
                    />
                  </View>
                  <CustomText
                    text={formatPercentage(student.progressPct)}
                    size="s"
                    useTranslationText={false}
                    style={styles.dataText}
                  />
                </View>
              </View>

              <View style={[styles.bodyCell, styles.colAssessments]}>
                <CustomText
                  text={formatPercentage(student.assessmentTakenPct)}
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: assessmentColor }}
                />
              </View>

              <View style={[styles.bodyCell, styles.colGrade]}>
                <CustomText text={aggregateGradeText} size="s" useTranslationText={false} style={styles.dataText} />
              </View>

              <View style={[styles.bodyCell, styles.colSentiment]}>
                <View
                  style={[
                    styles.pill,
                    { backgroundColor: sentimentBadge.backgroundColor, borderColor: sentimentBadge.borderColor },
                  ]}>
                  <CustomText
                    text={formatSentimentLabel(student.sentiment)}
                    size="xs"
                    weight="medium"
                    useTranslationText={false}
                    style={{ color: sentimentBadge.textColor }}
                  />
                </View>
              </View>

              <View style={[styles.bodyCell, styles.colActive]}>
                <CustomText
                  text={formatLastActive(student.lastActiveAt, now)}
                  size="s"
                  useTranslationText={false}
                  style={styles.dataText}
                />
              </View>
            </Pressable>
          );
        })
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  table: {
    backgroundColor: colors.white,
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.grey[50],
    paddingHorizontal: spacing.md,
  },
  headerLabel: {
    color: colors.grey[700],
    letterSpacing: 0.3,
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    minHeight: 75,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.white,
  },
  lastRow: {
    borderBottomWidth: 0,
  },
  selectedRow: {
    backgroundColor: "#EEF0FF",
  },
  headerCell: {
    justifyContent: "center",
    paddingVertical: 12,
  },
  bodyCell: {
    justifyContent: "center",
    paddingVertical: spacing.sm,
  },
  colCheckbox: {
    flex: 0.42,
  },
  colStudent: {
    flex: 2.45,
  },
  colRisk: {
    flex: 1.15,
  },
  colProgress: {
    flex: 1.25,
  },
  colAssessments: {
    flex: 1.15,
  },
  colGrade: {
    flex: 1.1,
  },
  colSentiment: {
    flex: 1.35,
  },
  colActive: {
    flex: 1.2,
  },
  studentCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: radii.full,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.brand[50],
    borderWidth: 1,
    borderColor: colors.brand[100],
  },
  avatarText: {
    color: colors.brand[600],
  },
  pill: {
    borderRadius: radii.full,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 5,
    alignSelf: "flex-start",
  },
  metricCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  metricTrack: {
    width: 42,
    height: 6,
    borderRadius: radii.full,
    overflow: "hidden",
    backgroundColor: colors.grey[200],
  },
  metricFill: {
    height: "100%",
    borderRadius: radii.full,
  },
  headerInline: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  dataText: {
    color: colors.grey[600],
  },
  mutedText: {
    color: colors.grey[500],
  },
  emptyState: {
    padding: spacing.lg,
    alignItems: "center",
  },
});

export default CourseRosterInsightsTable;
